import React, { useEffect, useState } from "react";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";

import { Box, Typography } from "@mui/material";
import EasyAnchorLink from "../../UI/AnchorLink";

import { GridColDef, GridInitialState } from "@mui/x-data-grid-premium";
import { renderNumberCell, renderWrappedCell } from "utils/dataGrid";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";

import "../BrandMetadataReview.css";
import PlacesDrillDownModal from "../UI/PlacesDrillDownModal";

interface GeneralBrandPlacesReviewProps {
  brand_id: string;
  country_code: string;
}

const ReviewPlacesRecurrentAddresses: React.FC<
  GeneralBrandPlacesReviewProps
> = ({ brand_id, country_code }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>([]);

  const { showError } = useError();
  const { apiRequest } = useApi();
  const defaultGridState: GridInitialState = {
    filter: {
      filterModel: {
        items: [
          {
            field: "count_recurrences",
            operator: ">",
            value: "1",
          },
        ],
      },
    },
    sorting: {
      sortModel: [
        { field: "count_recurrences", sort: "desc" },
        { field: "address", sort: "asc" },
      ],
    },
    pagination: { paginationModel: {} },
    pinnedColumns: {},
    columns: { columnVisibilityModel: {} },
    density: "standard",
    aggregation: {},
  };
  const columns_def: GridColDef[] = [
    {
      field: "address",
      headerName: "Address",
      description: "An address value associated with this brand and country.",
      type: "string",
      width: 300,
    },
    {
      field: "count_recurrences",
      headerName: "Recurrence Count",
      description:
        "The number of times this address appears in the places table for this brand and country.",
      type: "number",
      width: 180,
      renderCell: renderNumberCell,
    },
    {
      field: "city_state_list",
      headerName: "City, State",
      description:
        " A list of distinct city and state permutations associated with this address value where city and state are not null.",
      type: "string",
      width: 200,
      renderCell: renderWrappedCell,
    },
    {
      field: "drill_down",
      headerName: "View Places",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const api = "/brand_places/get_places_by_address";
        const apiParams = {
          brand_id: brand_id,
          country_code: country_code,
          address: params.row.address,
        };

        return (
          <>
            <PlacesDrillDownModal api={api} apiParams={apiParams} />
          </>
        );
      },
    },
  ];

  useEffect(() => {
    if (brand_id && country_code) {
      setLoading(true);
      const params = new URLSearchParams({
        brand_id: brand_id,
        country_code: country_code,
      });

      const fetchAddressRecurrence = async () => {
        try {
          const response = await apiRequest<any>(
            `/brand_places/get_address_recurrences?${params.toString()}`,
            {
              method: "GET",
            }
          );
          if (response.data) {
            setData(response.data);
          }
        } catch (error) {
          console.error(error);
          showError(
            `Failed to fetch places address recurrence for brand_id ${brand_id} + ${country_code}.`
          );
        } finally {
          setLoading(false);
        }
      };
      fetchAddressRecurrence();
    } else {
      setData([]);
    }
  }, [brand_id, country_code]);

  return (
    <Box className="metadata-review-container">
      <Box display="flex" alignItems="center" id="poi-addresses">
        <EasyAnchorLink id="poi-addresses" />
        <Typography variant="h6" gutterBottom sx={{ fontSize: "1.25rem" }}>
          Place Addresses Recurrence Count
        </Typography>
      </Box>

      <GaneshDataGrid
        gridId="placesReviewAddressRecurrence"
        defaultGridState={defaultGridState}
        columns={columns_def}
        data={data}
        loading={loading}
        headerFilters={data.length > 0 ? true : false}
        customHeight={data.length > 0 ? "750px" : "300px"}
      />
    </Box>
  );
};

export default ReviewPlacesRecurrentAddresses;
